import { useEffect, useRef } from 'react';

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
}

const TiktokPixel = ({ pixelId, marketingCookies }: PixelProps) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted) return;

      try {
        const trimmedId = pixelId?.trim();
        // validate pixel id is alphanumeric and at least 10 characters long
        if (!trimmedId?.match(/^[A-Z0-9]{10,}$/) || !marketingCookies) {
          return;
        }

        // Prevent duplicate script injection
        if (document.getElementById('tiktok-pixel')) {
          return;
        }

        const script = document.createElement('script');
        script.id = 'tiktok-pixel';
        script.async = true;
        script.innerHTML = `
          (function(){try{
              !function (w, d, t) {
                w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
                ttq.load('${trimmedId}');
                ttq.page(); // initial page view event
              }(window, document, 'ttq');
          }catch{/*swallow exception*/}})();
        `;
        document.head.appendChild(script);
        scriptRef.current = script;
      } catch {
        /*swallow exception*/
      }
    };

    // Defer pixel loading with timeout to ensure it fires
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    // Return cleanup function
    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      try {
        if (scriptRef.current?.parentNode) {
          scriptRef.current.parentNode.removeChild(scriptRef.current);
        }
      } catch {
        // Script already removed, safe to ignore
      }
    };
  }, [pixelId, marketingCookies]);

  return null;
};

export default TiktokPixel;
